import { LayerType, type Layer, type LayerStyle } from '@pn/core/domain/layer';
import { getHighlightedLayerId } from '@pn/core/domain/workspace';
import { mapboxLayerMapper } from '@pn/services/map/mapbox/mappers/mapboxLayerMapper';
import { colord, isValidColorString } from '@pn/services/utils/color';
import { clamp, isArray, isString } from 'lodash-es';

export function buildHighlightLayer(params: {
  layer: Layer;
  sourceId: string;
  style: LayerStyle;
}): mapboxgl.AnyLayer {
  const { layer, sourceId, style } = params;

  const highlightLayer: Layer = {
    ...layer,
    id: getHighlightedLayerId(layer.id),
    source: sourceId as any,
    style: generateHighlightedStyle(layer.type, style),
  };

  return mapboxLayerMapper.toTargetLayer(highlightLayer) as mapboxgl.AnyLayer;
}

function generateHighlightedStyle(
  layerType: LayerType,
  style: LayerStyle
): LayerStyle {
  if (layerType !== LayerType.Polygon) return style;

  return {
    ...style,
    color: darkenColor(style.color),
    opacity: ['case', ['==', ['feature-state', 'hover'], true], 1, 0],
  };
}

function darkenColor(color: unknown): unknown {
  if (!isArray(color)) {
    if (isString(color)) {
      return darkenColorString(color);
    } else {
      throw new Error('Invalid color argument');
    }
  }

  return color.map((c) =>
    isString(c) && isValidColorString(c) ? darkenColorString(c) : c
  );
}

/**
 * Makes a translucent color more opaque, or an opaque one 25% darker.
 */
function darkenColorString(color: string): string {
  const c = colord(color);
  const alpha = c.alpha();
  if (alpha < 1) {
    return c.alpha(clamp(alpha + 0.25, 1)).toRgbString();
  } else {
    const hsl = c.toHsl();
    return colord({ ...hsl, l: hsl.l * 0.75 }).toRgbString();
  }
}
